// src/pages/SalesRequestsManagement.jsx
import React, { useState, useEffect } from 'react';
import masaLogo from '../Assets/MasaLogo.png';
import styles from './SalesRequestsManagement.module.css'; 
import { 
  getAllSalesRequests, 
  updateSalesRequest, 
  deleteSalesRequest, 
} from '../api/salesRequestApi';

const SalesRequestsManagement = () => {
  const [requests, setRequests] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  const [editRequestId, setEditRequestId] = useState(null);
  const [editStatus, setEditStatus] = useState('');
  const [editAction, setEditAction] = useState(''); // New state for action type
  const [updatedSubmissionDate, setUpdatedSubmissionDate] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    // Fetch all active sales requests
    getAllSalesRequests()
      .then(response => setRequests(response.data))
      .catch(error => {
        console.error('Error fetching sales requests:', error);
        setErrorMessage('Failed to fetch sales requests. Please try again.');
      });
  }, []);

  const handleActionClick = (id, status, action) => {
    setEditRequestId(id);
    setEditStatus(status);
    setEditAction(action);
    setUpdatedSubmissionDate('');
    setNotes('');
  };

  const handleSaveStatus = async () => {
    try {
      let payload = { status: editStatus, notes: notes || undefined };

      if (editStatus === 'date-changed') {
        if (!updatedSubmissionDate) {
          setErrorMessage('Please select a new submission date.');
          return;
        }
        payload.updatedSubmissionDate = updatedSubmissionDate;
      }

      // If action is 'add' and status is 'held', treat it as 'accepted'
      if (editStatus === 'held' && editAction === 'add') {
        payload.status = 'accepted';
      }

      const response = await updateSalesRequest(editRequestId, payload);
      const updatedRequest = response.data;

      if (payload.status === 'held') {
        // Remove the held request from active requests
        setRequests(prev => prev.filter(r => r._id !== editRequestId));
        setModalMessage(`Request ${editRequestId} has been accepted and held.`);
      } else if (payload.status === 'accepted' && editAction === 'add') {
        // For 'add' actions, keep it in active requests
        setRequests(prev => prev.map(r => r._id === editRequestId ? updatedRequest : r));
        setModalMessage(`Request ${editRequestId} has been accepted and inventory updated.`);
      } else {
        // Update the request in the active requests list
        setRequests(prev => prev.map(r => r._id === editRequestId ? updatedRequest : r));
        setModalMessage(`Request ${editRequestId} has been ${payload.status}.`);
      }
      setShowModal(true);

      // Reset edit form
      setEditRequestId(null);
      setEditStatus('');
      setEditAction('');
      setUpdatedSubmissionDate('');
      setNotes('');
    } catch (error) {
      console.error('Error updating sales request:', error);
      setErrorMessage('Failed to update the sales request. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteSalesRequest(id);
      setRequests(prev => prev.filter(req => req._id !== id));
      setModalMessage(`Request ${id} has been deleted.`);
      setShowModal(true);
    } catch (error) {
      console.error('Error deleting sales request:', error);
      setErrorMessage('Failed to delete the sales request. Please try again.');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setModalMessage('');
  };

  return (
    <main className={styles.requestsManagement}>
      <header className={styles.header}>
        <img
          loading="lazy"
          src={masaLogo}
          className={styles.logo}
          alt="Dashboard Logo"
        />
        <h1 className={styles.title}>Sales Requests Management</h1>
      </header>
      <div className={styles.explanationText}>
        <p>Below are the active requests from the sales team. You can accept and release, accept and hold (only for removal actions), change the submission date, reject, or delete each request.</p>
      </div>
      {errorMessage && (
        <div className={styles.errorBanner}>{errorMessage}</div>
      )}
      
      {/* Active Sales Requests */}
      <section className={styles.tableContainer}>
        <h2>Active Sales Requests</h2>
        {requests.length === 0 ? (
          <p>No active sales requests found.</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Sales User</th>
                <th>Product</th>
                <th>Sort</th>
                <th>Color</th>
                <th>Action</th>
                <th>Quantity</th>
                <th>Submission Date</th>
                <th>Status</th>
                <th>Manager Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((request) => (
                <tr key={request._id}>
                  <td>{request._id}</td>
                  <td>{request.salesUser?.username || 'N/A'}</td>
                  <td>{request.product}</td>
                  <td>{request.sortName}</td>
                  <td>{request.color}</td>
                  <td>{request.action}</td>
                  <td>{request.quantity}</td>
                  <td>{new Date(request.submissionDate).toLocaleDateString()}</td>
                  <td>{request.status}</td>
                  <td>{request.managerResponse?.notes || ''}</td>
                  <td>
                    <button 
                      className={styles.acceptReleaseButton} 
                      onClick={() => handleActionClick(request._id, 'accepted', request.action)} 
                      disabled={!['pending', 'date-changed'].includes(request.status)}
                    >
                      Accept and Release
                    </button>
                    {/* Show "Accept and Hold" only for 'remove' actions */}
                    {request.action === 'remove' && (
                      <button 
                        className={styles.acceptHoldButton} 
                        onClick={() => handleActionClick(request._id, 'held', request.action)} 
                        disabled={!['pending', 'date-changed'].includes(request.status)}
                      >
                        Accept and Hold
                      </button>
                    )}
                    <button 
                      className={styles.changeDateButton} 
                      onClick={() => handleActionClick(request._id, 'date-changed', request.action)} 
                      disabled={!['pending', 'date-changed'].includes(request.status)}
                    >
                      Change Date
                    </button>
                    <button 
                      className={styles.rejectButton} 
                      onClick={() => handleActionClick(request._id, 'rejected', request.action)} 
                      disabled={!['pending', 'date-changed'].includes(request.status)}
                    >
                      Reject
                    </button>
                    <button 
                      className={styles.deleteButton} 
                      onClick={() => handleDelete(request._id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Modal for Status Update */}
      {(editStatus && editRequestId) && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalContent}>
            <h2>Update Request Status</h2>
            <p>
              {editStatus === 'accepted' && editAction === 'add' && `Set status to <strong>Accepted</strong> for request ID: ${editRequestId}. This will add the quantity directly to the inventory.`}
              {editStatus === 'accepted' && editAction !== 'add' && `Set status to <strong>Accepted and Released</strong> for request ID: ${editRequestId}.`}
              {editStatus === 'held' && `Set status to <strong>Accepted and Held</strong> for request ID: ${editRequestId}. This will reserve the quantity.`}
              {editStatus === 'date-changed' && `Set status to <strong>Date Changed</strong> for request ID: ${editRequestId}.`}
              {editStatus === 'rejected' && `Set status to <strong>Rejected</strong> for request ID: ${editRequestId}.`}
            </p>
            {(editStatus === 'held' || editStatus === 'date-changed') && (
              <>
                <label className={styles.formLabel}>
                  {editStatus === 'held' ? 'Hold Until Date:' : 'New Submission Date:'}
                </label>
                <input 
                  type="date" 
                  value={updatedSubmissionDate} 
                  onChange={(e) => setUpdatedSubmissionDate(e.target.value)} 
                  required 
                  className={styles.formInput}
                />
              </>
            )}
            <label className={styles.formLabel}>Manager Notes:</label>
            <textarea 
              value={notes} 
              onChange={(e) => setNotes(e.target.value)} 
              placeholder="Enter notes (optional)" 
              className={styles.formInput}
            />
            <div className={styles.modalButtonGroup}>
              <button onClick={handleSaveStatus} className={styles.confirmButton}>Save</button>
              <button onClick={() => {setEditStatus(''); setEditRequestId(null); setEditAction(''); setErrorMessage(null);}} className={styles.cancelButton}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Success Messages */}
      {showModal && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalContent}>
            <p>{modalMessage}</p>
            <button className={styles.closeButton} onClick={closeModal}>
              Close
            </button>
          </div>
        </div>
      )}
    </main>
  );
};

export default SalesRequestsManagement;
